import Vue from 'vue'
import store from '@/store'
import { initGlobalTheme } from '@/utils/theme'
import { installCommonPlugins } from '@/bootstrap/installCommonPlugins'
import MultiPageShell from '@/multi/MultiPageShell.vue'
import { setWebpackPublicPath } from '@/multi/setPublicPath'
import {
  applyRouteMeta,
  getCurrentRoute,
  getRouterOptionsRoutes,
  resolveHref
} from '@/multi/pageRoutes'

function isFileProtocol() {
  return typeof window !== 'undefined' && window.location && window.location.protocol === 'file:'
}

function navigateWithNativeLink(href, replace = false) {
  const anchor = document.createElement('a')
  anchor.href = href

  if (replace) {
    anchor.addEventListener('click', event => {
      event.preventDefault()
      window.location.replace(anchor.href)
    }, { once: true })
  }

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

function navigateTo(location, replace = false) {
  const href = resolveHref(location)

  if (isFileProtocol()) {
    navigateWithNativeLink(href, replace)
    return
  }

  if (replace) {
    window.location.replace(href)
    return
  }

  window.location.assign(href)
}

function createRouterShim(currentRoute) {
  return {
    options: {
      routes: getRouterOptionsRoutes()
    },
    currentRoute,
    push(location) {
      navigateTo(location, false)
    },
    replace(location) {
      navigateTo(location, true)
    },
    back() {
      window.history.back()
    },
    resolve(location) {
      return {
        location,
        route: currentRoute,
        href: resolveHref(location),
        normalizedTo: location,
        resolved: currentRoute
      }
    }
  }
}

function installRouterLink(router) {
  Vue.component('router-link', {
    inheritAttrs: false,
    props: {
      to: {
        type: [String, Object],
        required: true
      }
    },
    render(h) {
      const vnodeData = this.$vnode && this.$vnode.data ? this.$vnode.data : {}
      const attrs = Object.assign({}, this.$attrs, {
        href: resolveHref(this.to)
      })

      return h('a', {
        class: vnodeData.class,
        staticClass: vnodeData.staticClass,
        style: vnodeData.style,
        attrs,
        on: Object.assign({}, vnodeData.on, {
          click: (event) => {
            if (vnodeData.on && typeof vnodeData.on.click === 'function') {
              vnodeData.on.click(event)
            }
            if (
              event.defaultPrevented ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.button !== 0
            ) {
              return
            }
            if (isFileProtocol()) {
              return
            }
            event.preventDefault()
            router.push(this.to)
          }
        })
      }, this.$slots.default)
    }
  })
}

function installRouterAccessors() {
  if (!Vue.__MULTI_PAGE_ROUTER_MIXIN_INSTALLED__) {
    Vue.mixin({
      beforeCreate() {
        if (this.$options.multiPageRouter) {
          this._routerRoot = this
          this._router = this.$options.multiPageRouter
          Vue.util.defineReactive(this, '_route', this.$options.multiPageRoute)
        } else {
          this._routerRoot = (this.$parent && this.$parent._routerRoot) || this
        }
      }
    })
    Vue.__MULTI_PAGE_ROUTER_MIXIN_INSTALLED__ = true
  }

  const routerDescriptor = Object.getOwnPropertyDescriptor(Vue.prototype, '$router')
  if (!routerDescriptor) {
    Object.defineProperty(Vue.prototype, '$router', {
      get() {
        return this._routerRoot && this._routerRoot._router
      }
    })
  }

  const routeDescriptor = Object.getOwnPropertyDescriptor(Vue.prototype, '$route')
  if (!routeDescriptor) {
    Object.defineProperty(Vue.prototype, '$route', {
      get() {
        return this._routerRoot && this._routerRoot._route
      }
    })
  }
}

function createMultiPageContext(pageName) {
  const currentRoute = getCurrentRoute(pageName)
  const router = createRouterShim(currentRoute)

  window.__IS_MULTI_PAGE__ = true
  window.__MULTI_PAGE_ROUTE__ = currentRoute
  applyRouteMeta(currentRoute)
  installRouterAccessors()
  installRouterLink(router)

  return { currentRoute, router }
}

function waitForPrerenderRequests() {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  const idleThreshold = 300
  const timeout = 10000
  const startTime = Date.now()
  let lastBusyTime = Date.now()

  return new Promise(resolve => {
    const check = () => {
      const pendingCount = Number(window.__PRERENDER_PENDING_REQUESTS__ || 0)

      if (pendingCount > 0) {
        lastBusyTime = Date.now()
      }

      if (pendingCount === 0 && Date.now() - lastBusyTime >= idleThreshold) {
        resolve()
        return
      }

      if (Date.now() - startTime >= timeout) {
        resolve()
        return
      }

      window.requestAnimationFrame(check)
    }

    check()
  })
}

function markPrerenderReady() {
  window.__PRERENDER_READY__ = true
  document.dispatchEvent(new Event('multi-page-rendered'))
}

export function createPageApp({ pageName, component }) {
  setWebpackPublicPath(pageName)
  installCommonPlugins({
    enableMeta: true,
    enableParticles: false,
    enableBaiduMap: false
  })
  const { currentRoute, router } = createMultiPageContext(pageName)
  initGlobalTheme()

  Vue.config.productionTip = false

  const app = new Vue({
    store,
    multiPageRouter: router,
    multiPageRoute: currentRoute,
    render(h) {
      return h(MultiPageShell, [h(component)])
    }
  }).$mount('#app')

  app.$nextTick(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        waitForPrerenderRequests().then(() => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              markPrerenderReady()
            })
          })
        })
      })
    })
  })

  return app
}
